/**
 * Flujos premium precargados para operaciones masivas (p. ej. configuración de flujos).
 *
 * `premiumOrderActionIds` se deriva de los `action` de los subprocesos en `processes`.
 * Cada `processes` se arma con {@link ./preconfigured-premium-flow-processes} a partir de
 * nombre/posición/responsable/imagen por proceso, tarjetas por defecto (`premiumCards`, mismo
 * criterio que “Configuración por defecto” en permisos de proceso) y subprocesos por `action`
 * y posición; mensajes y notificaciones salen de {@link PREMIUM_ORDER_ACTIONS}.
 */
import { OrderProcessStructure } from '@app/core/services/api/API.service';
import { OrderAction } from './PREMIUM_ORDER_ACTIONS';
import {
    PRECONFIGURED_COUPA_PROCESSES,
    PRECONFIGURED_OC_PROCESSES,
    PRECONFIGURED_OC_WITH_AUTHORIZATION_PROCESSES_DOUBLE,
    PRECONFIGURED_OC_WITH_AUTHORIZATION_PROCESSES_SINGLE,
    PRECONFIGURED_TDC_TRANSFER_PROCESSES,
    buildHibridoCoupaProcesses
} from './preconfigured-premium-flow-processes';

/** Id del flujo «OC con autorización» (checkbox opcional de doble pasada). */
export const OC_AUTORIZACION_FLOW_ID = 'oc_autorizacion';

function collectPremiumOrderActionIds(
    processes: ReadonlyArray<OrderProcessStructure>
): OrderAction['id'][] {
    const ids = new Set<number>();
    for (const p of processes) {
        for (const s of p.subprocesses || []) {
            if (s?.action != null) {
                ids.add(s.action);
            }
        }
    }
    return Array.from(ids).sort((a, b) => a - b);
}

const HIBRIDO_COUPA_PROCESSES = buildHibridoCoupaProcesses();

export interface PreconfiguredPremiumFlowOption {
    id: string;
    label: string;
    /** Ids de acción (`OrderSubprocessStructure.action`) presentes en la plantilla. */
    premiumOrderActionIds: ReadonlyArray<OrderAction['id']>;
    /** Plantilla de procesos (estructura tipo `PremiumOrder.processes`). */
    processes: ReadonlyArray<OrderProcessStructure>;
    /**
     * Si existe, la UI puede ofrecer «Doble autorización» y usar esta plantilla
     * cuando el usuario la active (en «Cotización»: 11, 24 y luego carga OC).
     */
    processesWithDoubleAuthorization?: ReadonlyArray<OrderProcessStructure>;
}

export const PRECONFIGURED_PREMIUM_FLOWS: PreconfiguredPremiumFlowOption[] = [
    {
        id: 'coupa',
        label: 'Flujo Coupa',
        premiumOrderActionIds: collectPremiumOrderActionIds(
            PRECONFIGURED_COUPA_PROCESSES
        ),
        processes: PRECONFIGURED_COUPA_PROCESSES
    },
    {
        id: 'oc',
        label: 'Flujo OC',
        premiumOrderActionIds: collectPremiumOrderActionIds(
            PRECONFIGURED_OC_PROCESSES
        ),
        processes: PRECONFIGURED_OC_PROCESSES
    },
    {
        id: OC_AUTORIZACION_FLOW_ID,
        label: 'Flujo OC con autorización',
        premiumOrderActionIds: collectPremiumOrderActionIds(
            PRECONFIGURED_OC_WITH_AUTHORIZATION_PROCESSES_DOUBLE
        ),
        processes: PRECONFIGURED_OC_WITH_AUTHORIZATION_PROCESSES_SINGLE,
        processesWithDoubleAuthorization: PRECONFIGURED_OC_WITH_AUTHORIZATION_PROCESSES_DOUBLE
    },
    {
        id: 'tdc_transfer',
        label: 'Flujo TDC: carrito (transferencia o tarjeta)',
        premiumOrderActionIds: collectPremiumOrderActionIds(
            PRECONFIGURED_TDC_TRANSFER_PROCESSES
        ),
        processes: PRECONFIGURED_TDC_TRANSFER_PROCESSES
    },
    {
        id: 'hibrido_coupa',
        label: 'Híbrido Coupa',
        premiumOrderActionIds: collectPremiumOrderActionIds(
            HIBRIDO_COUPA_PROCESSES
        ),
        processes: HIBRIDO_COUPA_PROCESSES
    }
];
